/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import { firebase } from "@/firebase/firebaseConfig.js";
import Store from "@/store/store";
import authService from "@/services/authService";
import { getCachedUser } from "@/services/userSyncService";
import ColorMixin from "@/mixins/ColorMixin.js";
import moment from "moment";
import { getFeatureCodeForPath } from "@/utils/FeatureCodeUtil";
const ENTERPRISE_INVITATES_PATH = "/enterprise-invites";
const ENTERPRISE_ADD_PATH = "/enterprise-add";
const FREE_PATHS = [
  "/enterprise/management/crews",
  "/enterprise/management/notification-list",
  "/login/enterprise",
  "/enterprise/live-map",
  ENTERPRISE_INVITATES_PATH,
  ENTERPRISE_ADD_PATH,
  "/enterprise-add",
  "/admin-account-certification",
  "/enterprise/addon/crew-assignment-management",
  "/login/mfa",
  "/enterprise/addon/crew-data-management",
];

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    // -- トップへリダイレクト
    {
      path: "/",
      redirect: _ => {
        return "/enterprise/live-map";
      },
    },

    // -- Login
    {
      path: "/login",
      name: "Login",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "enterprise",
          name: "login-enterprise",
          component: () => import("@/views/enterprise/auth/Login.vue"),
        },
        {
          path: "enterprise/:enterpriseId",
          name: "login-enterprise-params",
          component: () => import("@/views/enterprise/auth/Login.vue"),
        },
        {
          // 2段階認証
          path: "mfa",
          name: "mfa",
          component: () => import("@/views/enterprise/auth/Mfa.vue"),
        },
      ],
    },
    // -- 法人メール登録
    {
      path: ENTERPRISE_INVITATES_PATH,
      name: "Invitaion",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "/",
          name: "invitation",
          component: () =>
            import("@/views/enterprise/enterprise-invites/Invitation.vue"),
        },
      ],
    },
    // -- 法人新規登録
    {
      path: ENTERPRISE_ADD_PATH,
      name: "EnterpriseAdd",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "/",
          name: "enterprise-add",
          component: () =>
            import("@/views/enterprise/enterprise-invites/EnterpriseAdd.vue"),
        },
      ],
    },
    // 管理者用法人adminアカウント管理
    {
      path: "/admin-account-certification",
      name: "AdminAccountCertification",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          // 管理者用法人adminアカウント発行
          path: "regist-admin-account",
          name: "admin-account-certification-regist-admin-account",
          component: () =>
            import(
              "@/views/enterprise/management/crews/admin-account-certification/RegistAdminAccount.vue"
            ),
        },
        {
          // 管理者用法人adminアカウントパスワード変更
          path: "change-password",
          name: "admin-account-certification-change-password",
          component: () =>
            import(
              "@/views/enterprise/management/crews/admin-account-certification/ChangePassword.vue"
            ),
        },
      ],
    },

    /*=========================================================================================
       法人
    ==========================================================================================*/
    {
      path: "/enterprise",
      name: "Enterprise",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "dashboard",
          name: "enterprise-dashboard",
          component: () => import("./views/enterprise/Dashboard.vue"),
        },
        // Map
        {
          path: "live-map",
          name: "enterprise-live-map",
          component: () => import("./views/enterprise/live-map/LiveMap.vue"),
          meta: {
            no_scroll: true,
          },
        },
        {
          path: "rg-object-history-player",
          name: "enterprise-rg-object-history-player",
          component: () =>
            import(
              "@/views/enterprise/rg-object-history/RgObjectHistoryPlayer.vue"
            ),
          meta: {
            no_scroll: true,
          },
        },
        // Plot
        {
          path: "plot-edit",
          name: "enterprise-plot-edit",
          component: () =>
            import("./views/enterprise/management/plot/Plot.vue"),
        },
        // Plot List
        {
          path: "plot-list",
          name: "enterprise-plot-list",
          component: () =>
            import("./views/enterprise/management/plot/PlotList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "プロット",
              },
              { title: "プロット一覧", active: true },
            ],
            pageTitle: "プロット一覧",
          },
        },

        /*=========================================================================================
          ツール -> 航海記録
        ==========================================================================================*/
        {
          path: "voyage-histories",
          name: "voyage-histories",
          component: () =>
            import("@/views/voyage-history/VoyageHistoryList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "航海記録",
              },
              { title: "航海記録一覧", active: true },
            ],
            pageTitle: "航海記録一覧",
          },
        },
        {
          path: "voyage-history-player",
          name: "voyage-history-player",
          component: () =>
            import("@/views/voyage-history/VoyageHistoryPlayer.vue"),
          meta: {
            breadcrumb: [
              {
                title: "航海記録",
              },
              { title: "航海記録一覧", url: { name: "voyage-histories" } },
              { title: "航海記録再生", active: true },
            ],
            pageTitle: "航海記録再生",
          },
        },
        /*=========================================================================================
          法人 -> 管理 -> 社員管理系
        ==========================================================================================*/
        {
          path: "management/user-list",
          name: "enterprise-management-user-list",
          component: () =>
            import("./views/enterprise/management/user/user-list/UserList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              { title: "社員一覧", active: true },
            ],
            pageTitle: "社員一覧",
          },
        },
        {
          path: "management/user-view/:userId",
          name: "enterprise-management-user-view",
          component: () =>
            import("./views/enterprise/management/user/user-view/UserView.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              { title: "社員詳細", active: true },
            ],
            parent: "enterprise-management-user-list",
            pageTitle: "社員詳細",
          },
        },
        {
          path: "management/user-edit/:userId",
          name: "enterprise-management-user-edit",
          component: () =>
            import("./views/enterprise/management/user/user-edit/UserEdit.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              {
                title: "社員詳細",
                url: { name: "enterprise-management-user-view" },
              },
              { title: "社員編集", active: true },
            ],
            parent: "enterprise-management-user-view",
            pageTitle: "社員編集",
          },
        },
        {
          path: "management/user-add",
          name: "enterprise-management-user-add",
          component: () =>
            import("./views/enterprise/management/user/user-add/UserAdd.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              { title: "社員追加", active: true },
            ],
            parent: "enterprise-management-user-list",
            pageTitle: "社員追加",
          },
        },
        {
          path: "management/user-sort",
          name: "enterprise-management-user-sort",
          component: () =>
            import("./views/enterprise/management/user/user-list/UserSort.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              { title: "社員表示順序", active: true },
            ],
            parent: "enterprise-management-user-list",
            pageTitle: "社員表示順序",
          },
        },
        {
          path: "management/license-add/:userId",
          name: "enterprise-management-license-add",
          component: () =>
            import(
              "./views/enterprise/management/user/license-add/LicenseAdd.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              {
                title: "社員詳細",
                url: { name: "enterprise-management-user-view" },
              },
              { title: "ライセンス追加", active: true },
            ],
            parent: "enterprise-management-user-view",
            pageTitle: "ライセンス追加",
          },
        },
        {
          path: "management/license-edit",
          name: "enterprise-management-license-edit",
          component: () =>
            import(
              "./views/enterprise/management/user/license-edit/LicenseEdit"
            ),
        },

        /*=========================================================================================
          法人 -> 管理 -> 船員管理系
        ==========================================================================================*/
        {
          path: "management/crews/crew-status-list",
          name: "enterprise-management-crew-status-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CrewLaborDashboard.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "労務ダッシュボード", active: true },
            ],
            pageTitle: "労務ダッシュボード",
            crewSettingNavigation: true,
            isCrewDashboardNavigationHidden: true,
            dispType: "corporate",
          },
        },

        {
          path: "management/crews/crew-labor-record/:id",
          name: "enterprise-management-crew-labor-record",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CrewLabor.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-list" },
              },
              { title: "勤怠情報", active: true },
            ],
            pageTitle: "勤怠情報",
            crewSettingNavigation: true,
            dispType: "labor",
            tabNumber: 0,
          },
        },
        {
          path: "management/crews/crew-timesheet-list/:id",
          name: "enterprise-management-crew-timesheet-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CrewLabor.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-list" },
              },
              { title: "打刻記録", active: true },
            ],
            pageTitle: "打刻記録",
            crewSettingNavigation: true,
            dispType: "labor",
            tabNumber: 1,
          },
        },
        {
          path: "management/crews/manning-vessels-list/:id",
          name: "enterprise-management-manning-vessels-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CrewLabor.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-list" },
              },
              { title: "乗船期間", active: true },
            ],
            pageTitle: "乗船期間",
            crewSettingNavigation: true,
            dispType: "labor",
            tabNumber: 2,
          },
        },
        {
          path: "management/crews/crew-labors/:id",
          name: "enterprise-management-crew-labors",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CrewLabor.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-list" },
              },
              { title: "労務情報", active: true },
            ],
            pageTitle: "労務情報",
            crewSettingNavigation: true,
            dispType: "labor",
            tabNumber: 3,
          },
        },
        {
          path: "management/crews/crew-profile/:id",
          name: "enterprise-management-crew-profile",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CrewLabor.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-list" },
              },
              { title: "船員基本情報", active: true },
            ],
            pageTitle: "船員基本情報",
            crewSettingNavigation: true,
            dispType: "labor",
            tabNumber: 4,
          },
        },

        {
          path: "management/crews/crew-labor-template",
          name: "enterprise-management-crew-labor-template",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "労務基本情報", active: true },
            ],
            pageTitle: "労務基本情報",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 0,
          },
        },
        {
          path: "management/crews/vessels-list",
          name: "enterprise-management-crew-vessels-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "船舶情報", active: true },
            ],
            pageTitle: "船舶情報",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 1,
          },
        },
        {
          path: "management/crews/crew-list",
          name: "enterprise-management-crew-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "船員一覧", active: true },
            ],
            pageTitle: "船員一覧",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 2,
          },
        },
        {
          path: "management/crews/admin-account-list",
          name: "enterprise-management-crew-admin-account-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "陸上従業員一覧", active: true },
            ],
            pageTitle: "陸上従業員一覧",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 3,
          },
        },
        {
          path: "management/crews/management-corp-manning-vessels-list",
          name: "enterprise-management-corp-manning-vessels-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "乗船期間一覧", active: true },
            ],
            pageTitle: "乗船期間一覧",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 4,
          },
        },
        // EL-2
        {
          path: "management/crews/admin-account-detail/:id",
          name: "enterprise-management-admin-account-detail",
          component: () =>
            import(
              "./views/enterprise/management/crews/admin-account-list/AdminAccountBasicInfoDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              {
                title: "陸上従業員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-admin-account-list" },
              },
              { title: "陸上従業員基本情報", active: true },
            ],
            pageTitle: "陸上従業員基本情報",
            crewSettingNavigation: true,
          },
        },
        {
          path: "management/crews/enterprise-management-task-kind-list",
          name: "enterprise-management-task-kind-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "作業種別管理", active: true },
            ],
            pageTitle: "作業種別管理",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 5,
          },
        },
        {
          path: "management/crews/vessel-movement-type-list",
          name: "enterprise-management-vessel-movement-type-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/page-layout-template/CorporateSetting.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "労務管理",
                color: "#2D9CDB",
              },
              { title: "船舶動静種別", active: true },
            ],
            pageTitle: "船舶動静種別",
            crewSettingNavigation: true,
            dispType: "corporate",
            tabNumber: 6,
          },
        },

        /*=========================================================================================
          法人 -> 管理 -> 船員管理系
        ==========================================================================================*/
        {
          // 船員管理 - 船員設定 - 船員一覧
          path: "management/crews/crew-settings/crew-list",
          name: "enterprise-management-crew-settings-list",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-list/CrewList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船員設定",
                color: "#626262",
              },
              { title: "船員一覧", active: true },
            ],
            pageTitle: "船員一覧",
            parent: "Crew_Settings",
          },
        },
        {
          // 船員管理 - 船員設定 - 船員新規登録
          path: "management/crews/crew-settings/add-crew",
          name: "enterprise-management-crew-settings-add",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-list/AddCrew.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船員設定",
                color: "#626262",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-settings-list" },
              },
              { title: "船員新規登録", active: true },
            ],
            pageTitle: "船員新規登録",
            parent: "Crew_Settings",
          },
        },
        {
          // 船員管理 - 船員設定 - 船員基本情報
          path: "management/crews/crew-settings/crew-basic-info/:id",
          name: "enterprise-management-crew-settings-info",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-detail/CrewDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船員設定",
                color: "#626262",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-settings-list" },
              },
              { title: "船員基本情報", active: true },
            ],
            pageTitle: "船員基本情報",
            parent: "Crew_Settings",
            selectedSubMenu: "CrewBasicInfo",
          },
        },
        {
          // 船員管理 - 船員設定 - 保有資格 - ダッシュボード
          path: "management/crews/crew-settings/license/dashboard/:id",
          name: "enterprise-management-crew-settings-license-dashboard",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-detail/CrewDetail.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "船員設定", color: "#626262" },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-settings-list" },
              },
              { title: "保有資格", color: "#626262" },
              { title: "ダッシュボード", active: true },
            ],
            pageTitle: "ダッシュボード",
            parent: "Crew_Settings",
            selectedSubMenu: "Dashboard",
          },
        },
        {
          // 船員管理 - 船員設定 - 保有資格 - 新規作成
          path: "management/crews/crew-settings/license/add/:id",
          name: "enterprise-management-crew-settings-license-add",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-detail/CrewDetail.vue"
            ),
          meta: {
            breadcrumb: [
              { title: "船員設定", color: "#626262" },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-settings-list" },
              },
              {
                title: "保有資格",
                color: "#626262",
              },
              {
                title: "ダッシュボード",
                color: "#2D9CDB",
                url: {
                  name: "enterprise-management-crew-settings-license-dashboard",
                },
              },
              { title: "新規作成", active: true },
            ],
            pageTitle: "新規作成",
            parent: "Crew_Settings",
            crewDetailViewMode: "FullDisplay",
          },
        },
        {
          // 船員管理 - 船員設定 - 保有資格 - 詳細
          path: "management/crews/crew-settings/license/:id",
          name: "enterprise-management-crew-settings-license",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-detail/CrewDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船員設定",
                color: "#626262",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-settings-list" },
              },
              { title: "保有資格", color: "#626262" },
              { title: "資格詳細", active: true },
            ],
            pageTitle: "保有資格",
            parent: "Crew_Settings",
          },
        },
        {
          // 船員管理 - 船員設定 - 配乗基本情報
          path: "management/crews/crew-settings/assignment-basic-info/:id",
          name: "enterprise-management-crew-settings-assignment",
          component: () =>
            import(
              "./views/enterprise/management/crews/crew-settings/crew-detail/CrewDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船員設定",
                color: "#626262",
              },
              {
                title: "船員一覧",
                color: "#2D9CDB",
                url: { name: "enterprise-management-crew-settings-list" },
              },
              { title: "配乗基本情報", active: true },
            ],
            pageTitle: "配乗基本情報",
            parent: "Crew_Settings",
            selectedSubMenu: "AssignmentBasicInfo",
          },
        },

        /*=========================================================================================
          法人 -> 運航管理 -> 運航船労務管理系
        ==========================================================================================*/
        // ダッシュボード
        {
          path:
            "management/crew-operator/enterprise-management-vessel-dashboard",
          name: "enterprise-management-vessel-dashboard",
          component: () =>
            import(
              "./views/enterprise/management/crew-operator/labor-dashboard/LaborDashboard.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "運航船労務管理",
                color: "#2D9CDB",
              },
              { title: "運航船労務ダッシュボード", active: true },
            ],
            pageTitle: "運航船労務ダッシュボード",
          },
        },

        // 運航船日時詳細
        {
          path: "management/crew-operator/daily-labor-record/:id",
          name: "enterprise-management-daily-labor-record",
          component: () =>
            import(
              "./views/enterprise/management/crew-operator/vessel-labor-detail/VesselLaborDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "運航船労務管理",
                color: "#2D9CDB",
              },
              {
                title: "運航船労務ダッシュボード",
                color: "#2D9CDB",
                url: { name: "enterprise-management-vessel-dashboard" },
              },
              { title: "運航船日次詳細", active: true },
            ],
            pageTitle: "運航船日次詳細",
            crewOperatorNavigation: true,
            tabNumber: 0,
          },
        },
        // 運航船月次詳細
        {
          path: "management/crew-operator/monthly-labor-record/:id",
          name: "enterprise-management-monthly-labor-record",
          component: () =>
            import(
              "./views/enterprise/management/crew-operator/vessel-labor-detail/VesselLaborDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "運航船労務管理",
                color: "#2D9CDB",
              },
              {
                title: "運航船労務ダッシュボード",
                color: "#2D9CDB",
                url: { name: "enterprise-management-vessel-dashboard" },
              },
              { title: "運航船月次詳細", active: true },
            ],
            pageTitle: "運航船月次詳細",
            crewOperatorNavigation: true,
            tabNumber: 1,
          },
        },

        //匿名勤怠情報
        {
          path: "management/crew-operator/crew-labor-detail/:id",
          name: "enterprise-management-crew-operator-crew-labor-detail",
          component: () =>
            import(
              "./views/enterprise/management/crew-operator/crew-labor-detail/CrewLaborDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "運航船労務管理",
                color: "#2D9CDB",
              },
              {
                title: "労務ダッシュボード",
                color: "#2D9CDB",
                url: { name: "enterprise-management-vessel-dashboard" },
              },
              { title: "勤怠情報", active: true },
            ],
            pageTitle: "勤怠情報",
            crewOperatorNavigation: true,
            tabNumber: 0,
          },
        },

        /*=========================================================================================
          法人 -> 管理 -> 船舶管理系
        ==========================================================================================*/
        {
          path: "management/vessel-list",
          name: "enterprise-management-vessel-list",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-list/VesselList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              { title: "船舶一覧", active: true },
            ],
            pageTitle: "船舶一覧",
          },
        },
        {
          path: "management/vessel-view/:vesselId",
          name: "enterprise-management-vessel-view",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-view/VesselView.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              {
                title: "船舶一覧",
                url: { name: "enterprise-management-vessel-list" },
              },
              {
                title: "船舶詳細",
                active: true,
              },
            ],
            parent: "enterprise-management-vessel-list",
            pageTitle: "船舶詳細",
          },
        },
        {
          path: "management/vessel-edit/:vesselId",
          name: "enterprise-management-vessel-edit",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-edit/VesselEdit.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              {
                title: "船舶一覧",
                url: { name: "enterprise-management-vessel-list" },
              },
              {
                title: "船舶詳細",
                url: { name: "enterprise-management-vessel-view" },
              },
              { title: "船舶編集", active: true },
            ],
            parent: "enterprise-management-vessel-view",
            pageTitle: "船舶編集",
          },
        },
        {
          path: "management/vessel-sort",
          name: "enterprise-management-vessel-sort",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-list/VesselSort.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              {
                title: "船舶一覧",
                url: { name: "enterprise-management-vessel-list" },
              },
              { title: "船舶表示順序", active: true },
            ],
            parent: "enterprise-management-vessel-list",
            pageTitle: "船舶表示順序",
          },
        },

        /*=========================================================================================
          法人 -> 管理 -> カメラ管理系
        ==========================================================================================*/
        {
          path: "management/camera-list",
          name: "enterprise-management-camera-list",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-list/CameraList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                active: true,
              },
            ],
            pageTitle: "カメラ一覧",
          },
        },
        {
          path: "management/camera-sort",
          name: "enterprise-management-camera-sort",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-list/CameraSort.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                url: { name: "enterprise-management-camera-list" },
              },
              { title: "カメラ表示順序", active: true },
            ],
            parent: "enterprise-management-camera-list",
            pageTitle: "カメラ表示順序",
          },
        },
        {
          path: "management/camera-view/:cameraId/realtime",
          name: "enterprise-management-camera-view-realtime",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-view/CameraViewRealtime.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                url: { name: "enterprise-management-camera-list" },
              },
              {
                title: "リアルタイムカメラ",
              },
              {
                title: "",
                active: true,
              },
            ],
            parent: "enterprise-management-camera-list",
            pageTitle: "リアルタイムカメラ",
          },
        },
        {
          path: "management/camera-view/:cameraId/history",
          name: "enterprise-management-camera-view-history",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-view/CameraViewHistory.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                url: { name: "enterprise-management-camera-list" },
              },
              {
                title: "航海履歴",
              },
              {
                title: "",
                active: true,
              },
            ],
            parent: "enterprise-management-camera-list",
            pageTitle: "航海履歴",
          },
        },

        /*=========================================================================================
         法人 -> 管理 -> お知らせ一覧
        ==========================================================================================*/
        {
          path: "management/notification-list",
          name: "enterprise-management-notification-list",
          component: () => import("./views/notification-list/NotificationList"),
          meta: {
            breadcrumb: [
              {
                title: "お知らせ管理",
              },
              {
                title: "お知らせ一覧",
                active: true,
              },
            ],
            pageTitle: "お知らせ一覧",
          },
        },
        /*=========================================================================================
         動静連絡
        ==========================================================================================*/
        {
          path: "addon/vessel-noon-report-detail/:vesselId/:reportId",
          name: "addon-vessel-noon-report-detail",
          component: () =>
            import(
              "./views/enterprise/addon/vessel-noon-report/report-detail/NoonReportDetail.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "一覧",
                url: { name: "addon-vessel-noon-report-list" },
              },
              {
                title: "詳細",
                active: true,
              },
            ],
            pageTitle: "動静連絡",
          },
        },
        /*=========================================================================================
         通知
        ==========================================================================================*/
        {
          path: "notification-list",
          redirect: "notification-list/all",
          name: "notification-list-all",
        },
        {
          path: "notification-list/:filter",
          name: "notification-list",
          component: () => import("./views/notification-list/NotificationList"),
          meta: {
            breadcrumb: [
              {
                title: "通知管理",
              },
              {
                title: "通知一覧",
                active: true,
              },
            ],
            pageTitle: "通知一覧",
          },
        },
        /*=========================================================================================
          動静連絡
         ==========================================================================================*/
        // 船舶動静連絡一覧
        {
          path: "addon/vessel-noon-report-list",
          name: "addon-vessel-noon-report-list",
          component: () =>
            import(
              "./views/enterprise/addon/vessel-noon-report/report-list/NoonReportList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "一覧",
                active: true,
              },
            ],
            pageTitle: "動静連絡",
          },
        },
        /*=========================================================================================
          機能拡張 -> 船舶レポート
         ==========================================================================================*/
        // 船舶レポート一覧
        {
          path: "addon/vessel-report-list",
          name: "addon-vessel-report-list",
          component: () =>
            import(
              "./views/enterprise/addon/vessel-report/vessel-report-list/VesselReportList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "一覧",
                active: true,
              },
            ],
            pageTitle: "船舶動静連絡",
          },
        },
        // レポート詳細
        {
          path: "addon/vessel-report-view",
          name: "addon-vessel-report-view",
          component: () =>
            import(
              "./views/enterprise/addon/vessel-report/vessel-report-view/VesselReportView"
            ),
          meta: {
            breadcrumb: [
              {
                title: "一覧",
                url: { name: "addon-vessel-report-list" },
              },
              {
                title: "詳細",
                active: true,
              },
            ],
            pageTitle: "船舶動静連絡",
          },
        },
        // レポートエクスポート
        {
          path: "addon/vessel-report-export",
          name: "addon-vessel-report-export",
          component: () =>
            import(
              "./views/enterprise/addon/vessel-report/vessel-report-export/VesselReportExport"
            ),
          meta: {
            breadcrumb: [
              {
                title: "集計",
                active: true,
              },
            ],
            pageTitle: "船舶動静連絡",
          },
        },
        /*=========================================================================================
          機能拡張 -> PortMaster
         ==========================================================================================*/
        // 入出港管理
        {
          path: "addon/port-master-port-menu",
          name: "addon-port-master-port-menu",
          component: () =>
            import("./views/enterprise/addon/port-master/port-menu/PortMenu"),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                active: true,
              },
            ],
            pageTitle: "入出港管理",
            portMasterNavigation: true,
          },
        },

        // 港湾選択
        {
          path: "addon/port-master-port-select",
          name: "addon-port-master-port-select",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/port-select/PortSelect"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "港湾選択",
                active: true,
              },
            ],
            pageTitle: "港湾選択",
            portMasterNavigation: true,
          },
        },

        // 船舶管理
        {
          path: "addon/port-master-vessel-master",
          name: "addon-port-master-vessel-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vessel-master/VesselMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                active: true,
              },
            ],
            pageTitle: "船舶管理",
            portMasterNavigation: true,
          },
        },
        // 船舶新規登録
        {
          path: "addon/port-master-vessel-insert",
          name: "addon-port-master-vessel-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vessel-master/VesselInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                url: { name: "addon-port-master-vessel-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "船舶管理",
            portMasterNavigation: true,
          },
        },
        // 船舶情報
        {
          path: "addon/port-master-vessel-view/:itemId",
          name: "addon-port-master-vessel-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vessel-master/VesselViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                url: { name: "addon-port-master-vessel-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶情報",
                active: true,
              },
            ],
            pageTitle: "船舶情報",
            portMasterNavigation: true,
          },
        },

        // バース管理
        {
          path: "addon/port-master-berth-master",
          name: "addon-port-master-berth-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/berth-master/BerthMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース管理一覧",
                active: true,
              },
            ],
            pageTitle: "バース管理",
            portMasterNavigation: true,
          },
        },
        // バース新規登録
        {
          path: "addon/port-master-berth-insert",
          name: "addon-port-master-berth-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/berth-master/BerthInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース管理一覧",
                url: { name: "addon-port-master-berth-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "バース管理",
            portMasterNavigation: true,
          },
        },
        // バース情報
        {
          path: "addon/port-master-berth-view/:itemId",
          name: "addon-port-master-berth-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/berth-master/BerthViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース管理一覧",
                url: { name: "addon-port-master-berth-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース情報",
                active: true,
              },
            ],
            pageTitle: "バース情報",
            portMasterNavigation: true,
          },
        },

        // 法人代理店管理
        {
          path: "addon/port-master-enterprise-agency-master",
          name: "addon-port-master-enterprise-agency-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/enterprise-agency-master/EnterpriseAgencyMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）管理一覧",
                active: true,
              },
            ],
            pageTitle: "代理店（法人）管理",
            portMasterNavigation: true,
          },
        },
        // 法人代理店新規登録
        {
          path: "addon/port-master-enterprise-agency-insert",
          name: "addon-port-master-enterprise-agency-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/enterprise-agency-master/EnterpriseAgencyInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）管理一覧",
                url: { name: "addon-port-master-enterprise-agency-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "代理店（法人）管理",
            portMasterNavigation: true,
          },
        },
        // 法人代理店情報
        {
          path: "addon/port-master-enterprise-agency-view/:itemId",
          name: "addon-port-master-enterprise-agency-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/enterprise-agency-master/EnterpriseAgencyViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）管理一覧",
                url: { name: "addon-port-master-enterprise-agency-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）情報",
                active: true,
              },
            ],
            pageTitle: "代理店（法人）情報",
            portMasterNavigation: true,
          },
        },

        // 配乗管理
        {
          path: "addon/crew-assignment-management",
          name: "addon-crew-assignment-management",
          component: () =>
            import(
              "./views/enterprise/addon/crew-assignment-management/crew-assignment-dashboard/CrewAssignmentDashboard"
            ),
          meta: {
            breadcrumb: [
              {
                title: "配乗管理",
                url: { name: "addon-crew-assignment-management" },
                active: true,
                color: "#039BE5",
              },
            ],
            pageTitle: "配乗管理ダッシュボード",
            crewAssignmentNavigation: true,
          },
        },
        // 配乗一覧表
        {
          path: "addon/crew-assignment-management/manning-schedule/overview",
          name: "addon-crew-assignment-management-manning-schedule-overview",
          component: () =>
            import(
              "./views/enterprise/addon/crew-assignment-management/crew-assignment-list/CrewAssignmentList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "配乗管理",
                color: "#626262",
              },
              {
                title: "配乗ダッシュボード",
                color: "#2D9CDB",
                url: { name: "addon-crew-assignment-management" },
              },
              {
                title: "配乗一覧表",
                active: true,
              },
            ],
            pageTitle: "配乗一覧表",
            crewAssignmentNavigation: true,
            // ナビゲーションメニューを非表示
            isVerticalNavMenuDeactive: true,
          },
        },
        // 船舶単位
        {
          path: "addon/crew-assignment-management/manning-schedule/vessels",
          name: "addon-crew-assignment-management-manning-schedule-vessels",
          component: () =>
            import(
              "@/views/enterprise/addon/crew-assignment-management/manning-schedule/vessels/Vessels"
            ),
          meta: {
            // ナビゲーションメニューを非表示
            isVerticalNavMenuDeactive: true,
          },
        },
        // 船員単位
        {
          path: "addon/crew-assignment-management/manning-schedule/crews",
          name: "addon-crew-assignment-management-manning-schedule-crews",
          component: () =>
            import(
              "@/views/enterprise/addon/crew-assignment-management/manning-schedule/crews/Crews"
            ),
          meta: {
            // ナビゲーションメニューを非表示
            isVerticalNavMenuDeactive: true,
          },
        },
        // 配乗管理 - 休暇買い上げ・繰越し
        {
          path: "addon/crew-assignment-management/day-off-purchase-carryover",
          name: "addon-crew-assignment-management-day-off-purchase-carryover",
          component: () =>
            import(
              "./views/enterprise/addon/crew-assignment-management/crew-assignment-day-off-purchase-carryover/CrewAssignmentDayOffPurchaseCarryover.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "配乗管理",
                color: "#626262",
              },
              {
                title: "配乗ダッシュボード",
                color: "#2D9CDB",
                url: { name: "addon-crew-assignment-management" },
              },
              {
                title: "休暇買上げ・繰越し",
                active: true,
              },
            ],
            pageTitle: "休暇買い上げ・繰越し",
            crewAssignmentNavigation: true,
          },
        },

        // データ入出力
        {
          path: "addon/crew-data-management",
          name: "addon-crew-data-management",
          component: () =>
            import(
              "./views/enterprise/addon/crew-data-management/CrewDataManagement"
            ),
          meta: {
            pageTitle: "データ入出力",
          },
        },

        // 代理店管理
        /* 次のフェーズまでコメントアウト
        {
          path: "addon/port-master-agency-master",
          name: "addon-port-master-agency-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/agency-master/AgencyMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false
              },
              {
                title: "代理店管理一覧",
                active: true
              }
            ],
            pageTitle: "代理店管理",
            portMasterNavigation: true
          }
        },
        // 代理店情報
        {
          path: "addon/port-master-agency-view/:itemId",
          name: "addon-port-master-agency-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/agency-master/AgencyViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false
              },
              {
                title: "代理店管理一覧",
                url: { name: "addon-port-master-agency-master" },
                active: false
              },
              {
                title: "代理店情報",
                active: true
              }
            ],
            pageTitle: "代理店情報",
            portMasterNavigation: true
          }
        },
        */

        // 入出港一覧表示
        {
          path: "addon/port-master/destination-work-display/:displayId",
          name: "addon-port-master-destination-work-display",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work/DestinationWorkList"
            ),
          meta: {
            isPortMasterFullScreen: true,
          },
        },
        // 入出港一覧管理
        {
          path: "addon/port-master/destination-work-edit/",
          name: "addon-port-master-destination-work-edit",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work-edit/DestinationWorkEdit"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "編集",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 項目順序変更
        {
          path: "addon/port-master/destination-work-column-edit/",
          name: "addon-port-master-destination-work-column-edit",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work-edit/EditColumnOrder"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "編集",
                url: { name: "addon-port-master-destination-work-edit" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "項目順序変更",
                active: true,
              },
            ],
            pageTitle: "項目順序変更",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 入出港オーダー表
        {
          path: "addon/port-master/destination-order-list/",
          name: "addon-port-master-destination-order-list",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-order-list/DestinationOrderList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 入出港一括取り込み
        {
          path: "addon/port-master/destination-order-import/",
          name: "addon-port-master-destination-order-import",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-order-import/DestinationOrderImport"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "予定表一括取込",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 一括取り込み予定表
        {
          path: "addon/port-master/destination-imported-work-edit",
          name: "addon-port-master-destination-imported-work-edit",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work-edit/DestinationImportedWorkEdit"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "登録",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },

        // 水先案内人管理
        {
          path: "addon/port-master-pilot-master",
          name: "addon-port-master-pilot-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/pilot-master/PilotMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "水先案内人管理一覧",
                active: true,
              },
            ],
            pageTitle: "水先案内人管理",
            portMasterNavigation: true,
          },
        },
        // 水先案内人新規登録
        {
          path: "addon/port-master-pilot-insert",
          name: "addon-port-master-pilot-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/pilot-master/PilotInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "水先案内人管理一覧",
                url: { name: "addon-port-master-pilot-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "水先案内人管理",
            portMasterNavigation: true,
          },
        },

        // 班長管理
        {
          path: "addon/port-master-team-leader-master",
          name: "addon-port-master-team-leader-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/team-leader-master/TeamLeaderMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "班長管理一覧",
                active: true,
              },
            ],
            pageTitle: "班長管理",
            portMasterNavigation: true,
          },
        },
        // 班長新規登録
        {
          path: "addon/port-master-team-leader-insert",
          name: "addon-port-master-team-leader-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/team-leader-master/TeamLeaderInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "班長管理一覧",
                url: { name: "addon-port-master-team-leader-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "班長管理",
            portMasterNavigation: true,
          },
        },

        // タグボート管理
        {
          path: "addon/port-master-tug-boat-master",
          name: "addon-port-master-tug-boat-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/tug-boat-master/TugBoatMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグボート管理一覧",
                active: true,
              },
            ],
            pageTitle: "タグボート管理",
            portMasterNavigation: true,
          },
        },
        // タグボート新規登録
        {
          path: "addon/port-master-tug-boat-insert",
          name: "addon-port-master-tug-boat-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/tug-boat-master/TugBoatInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグボート管理一覧",
                url: { name: "addon-port-master-tug-boat-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "タグボート管理",
            portMasterNavigation: true,
          },
        },

        // 車両管理
        {
          path: "addon/port-master-vehicle-master",
          name: "addon-port-master-vehicle-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vehicle-master/VehicleMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "車両管理一覧",
                active: true,
              },
            ],
            pageTitle: "車両管理",
            portMasterNavigation: true,
          },
        },
        // 車両新規登録
        {
          path: "addon/port-master-vehicle-insert",
          name: "addon-port-master-vehicle-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vehicle-master/VehicleInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "車両管理一覧",
                url: { name: "addon-port-master-vehicle-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "車両管理",
            portMasterNavigation: true,
          },
        },

        // 綱取りボート管理
        {
          path: "addon/port-master-rope-picking-boat-master",
          name: "addon-port-master-rope-picking-boat-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/rope-picking-boat-master/RopePickingBoatMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "綱ボート管理一覧",
                active: true,
              },
            ],
            pageTitle: "綱ボート管理",
            portMasterNavigation: true,
          },
        },
        // 綱取りボート新規登録
        {
          path: "addon/port-master-rope-picking-boat-insert",
          name: "addon-port-master-rope-picking-boat-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/rope-picking-boat-master/RopePickingBoatInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "綱ボート管理一覧",
                url: { name: "addon-port-master-rope-picking-boat-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "綱ボート管理",
            portMasterNavigation: true,
          },
        },

        // タグ管理
        {
          path: "addon/port-master-tag-master",
          name: "addon-port-master-tag-master",
          component: () =>
            import("./views/enterprise/addon/port-master/tag-master/TagMaster"),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグ管理一覧",
                active: true,
              },
            ],
            pageTitle: "タグ管理",
            portMasterNavigation: true,
          },
        },
        // タグ新規登録
        {
          path: "addon/port-master-tag-insert",
          name: "addon-port-master-tag-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/tag-master/TagInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグ管理一覧",
                url: { name: "addon-port-master-tag-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "タグ管理",
            portMasterNavigation: true,
          },
        },

        /*=========================================================================================
          機能拡張 -> EcoShip
         ==========================================================================================*/

        // タンク液位管理
        {
          path: "addon/eco-ship/tank-liquid-level",
          name: "addon-eco-ship-tank-liquid-level",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/tank-liquid-level/TankLiquidLevel"
            ),
          meta: {
            breadcrumb: [
              {
                title: "タンク液位管理",
                color: "#2D9CDB",
              },
              {
                title: "タンク液位一覧",
                active: true,
              },
            ],
            pageTitle: "タンク液位管理",
          },
        },

        // リーファーコンテナ管理一覧
        {
          path: "addon/eco-ship-vessel-container-master",
          name: "addon-eco-ship-vessel-container-master",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/vessel-container/VesselContainerMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "リーファーコンテナ管理",
                color: "#2D9CDB",
              },
              {
                title: "リーファーコンテナ一覧",
                active: true,
              },
            ],
            pageTitle: "リーファーコンテナ管理",
          },
        },
        // リーファーコンテナ管理詳細
        {
          path: "addon/eco-ship-vessel-container-details",
          name: "addon-eco-ship-vessel-container-details",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/vessel-container/VesselContainerDetails"
            ),
          meta: {
            breadcrumb: [
              {
                title: "リーファーコンテナ管理",
                color: "#2D9CDB",
              },
              {
                title: "リーファーコンテナ一覧",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-vessel-container-master" },
              },
              {
                title: "詳細",
                active: true,
              },
            ],
            pageTitle: "リーファーコンテナ管理",
          },
        },

        // コンテナ顧客一覧
        {
          path: "addon/eco-ship-container-customer-master",
          name: "addon-eco-ship-container-customer-master",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/container-customer-master/ContainerCustomerMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "リーファーコンテナ管理",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-vessel-container-master" },
              },
              {
                title: "顧客一覧",
                active: true,
              },
            ],
            pageTitle: "顧客一覧",
          },
        },
        // コンテナ顧客詳細
        {
          path: "addon/eco-ship-container-customer-view/:itemId",
          name: "addon-eco-ship-container-customer-view",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/container-customer-master/ContainerCustomerViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "リーファーコンテナ管理",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-vessel-container-master" },
              },
              {
                title: "顧客一覧",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-container-customer-master" },
              },
              {
                title: "顧客情報",
                active: true,
              },
            ],
            pageTitle: "顧客一覧",
          },
        },
        // コンテナ顧客新規
        {
          path: "addon/eco-ship-container-customer-insert",
          name: "addon-eco-ship-container-customer-insert",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/container-customer-master/ContainerCustomerInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "リーファーコンテナ管理",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-vessel-container-master" },
              },
              {
                title: "顧客一覧",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-container-customer-master" },
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "顧客一覧",
          },
        },
        // コンテナ顧客顧客別稼働状況
        {
          path: "addon/eco-ship-container-customer-summary",
          name: "addon-eco-ship-container-customer-summary",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/container-customer-master/ContainerCustomerSummary"
            ),
          meta: {
            breadcrumb: [
              {
                title: "リーファーコンテナ管理",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-vessel-container-master" },
                active: false,
              },
              {
                title: "顧客リスト",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-container-customer-master" },
              },
              {
                title: "コンテナ稼働状況",
                active: true,
              },
            ],
            pageTitle: "リーファーコンテナ管理",
          },
        },

        // 航路計画一覧
        {
          path: "addon/eco-ship-voyage-plan-list",
          name: "addon-eco-ship-voyage-plan-list",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/voyage-plan/VoyagePlanList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "航路計画管理",
                color: "#2D9CDB",
              },
              {
                title: "航路計画一覧",
                active: true,
              },
            ],
            pageTitle: "航路計画一覧",
          },
        },
        // 航路計画詳細
        {
          path: "addon/eco-ship-voyage-plan-detail",
          name: "addon-eco-ship-voyage-plan-detail",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/voyage-plan/VoyagePlanDetail"
            ),
          meta: {
            breadcrumb: [
              {
                title: "航路計画管理",
                color: "#2D9CDB",
              },
              {
                title: "航路計画一覧",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-voyage-plan-list" },
              },
              {
                title: "航路計画詳細",
                active: true,
              },
            ],
            pageTitle: "航路計画詳細",
          },
        },
        // 航路計画詳細（複数航路比較）
        {
          path: "addon/eco-ship-voyage-plan-compare",
          name: "addon-eco-ship-voyage-plan-compare",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/voyage-plan/VoyagePlanCompare"
            ),
          meta: {
            breadcrumb: [
              {
                title: "航路計画管理",
                color: "#2D9CDB",
              },
              {
                title: "航路計画一覧",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-voyage-plan-list" },
              },
              {
                title: "航路計画詳細",
                active: true,
              },
            ],
            pageTitle: "航路計画詳細",
          },
        },

        // eco-ship機関データ一覧
        {
          path: "addon/eco-ship/vessel-monitoring-engine-list",
          name: "addon-eco-ship-vessel-monitoring-engine-list",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-monitoring-engine/vesselMonitoringEngineList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "機関データ",
                color: "#2D9CDB",
              },
              {
                title: "一覧",
                active: true,
              },
            ],
            pageTitle: "機関データ",
          },
        },
        // eco-ship機関データ詳細データレポート
        {
          path: "addon/eco-ship/vessel-monitoring-engine/:id",
          name: "addon-eco-ship-vessel-monitoring-engine",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-monitoring-engine/vesselMonitoringEngine"
            ),
          meta: {
            breadcrumb: [
              {
                title: "機関データ",
                color: "#2D9CDB",
              },
              {
                title: "一覧",
                color: "#2D9CDB",
                url: { name: "addon-eco-ship-vessel-monitoring-engine-list" },
              },
              {
                title: "詳細",
                active: true,
              },
            ],
            pageTitle: "機関データ",
          },
        },

        // 燃料消費量一覧
        {
          path: "addon/eco-ship-fuel-consumption-list",
          name: "addon-eco-ship-fuel-consumption-list",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/fuel-consumption/FuelConsumptionList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "燃料消費量",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
              },
              {
                title: "航海一覧",
                active: true,
              },
            ],
            pageTitle: "航海一覧",
          },
        },
        // 燃料消費量詳細
        {
          path: "addon/eco-ship-fuel-consumption-detail",
          name: "addon-eco-ship-fuel-consumption-detail",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/fuel-consumption/FuelConsumptionDetail"
            ),
          meta: {
            breadcrumb: [
              {
                title: "燃料消費量",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
              },
              {
                title: "航海一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "addon-eco-ship-fuel-consumption-list" },
              },
              {
                title: "燃料消費量レポート",
                active: true,
              },
            ],
            pageTitle: "燃料消費量レポート",
          },
        },
        // 燃料消費量月次レポート
        {
          path: "addon/eco-ship-fuel-consumption-monthly-fuel-report",
          name: "addon-eco-ship-fuel-consumption-monthly-fuel-report",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/fuel-consumption/MonthlyFuelReportList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "燃料消費量",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
              },
              {
                title: "月次レポート",
                active: true,
              },
            ],
            pageTitle: "燃料消費量(月次レポート)",
          },
        },
        // 燃料消費量統計レポート
        {
          path: "addon/eco-ship-fuel-consumption-yearly-fuel-report",
          name: "addon-eco-ship-fuel-consumption-yearly-fuel-report",
          component: () =>
            import(
              "./views/enterprise/addon/eco-ship/fuel-consumption/YearlyFuelReportList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "燃料消費量",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
              },
              {
                title: "統計レポート",
                active: true,
              },
            ],
            pageTitle: "燃料消費量(統計レポート)",
          },
        },
        /*=========================================================================================
         機能拡張 -> 船舶監視
        ==========================================================================================*/
        // 操船監視画面
        {
          path: "addon/secure-dataflow/monitoring-vessel-handling",
          name: "addon-secure-dataflow-monitoring-vessel-handling",
          component: () =>
            import(
              "./views/enterprise/addon/secure-dataflow/monitoring-vessel-maneuvering/monitoringVesselManeuvering"
            ),
          meta: {
            isSecureDataflow: true,
          },
        },
        /*=========================================================================================
         機能拡張 -> 機関監視トレンドデータ
        ==========================================================================================*/
        // 機関監視トレンドデータ画面
        {
          path: "addon/secure-dataflow/monitoring-vessel-engine",
          name: "addon-secure-dataflow-monitoring-vessel-engine",
          component: () =>
            import(
              "./views/enterprise/addon/secure-dataflow/monitoring-vessel-engine/monitoringVesselEngine"
            ),
          meta: {
            isSecureDataflow: true,
          },
        },
        /*=========================================================================================
         機能拡張 -> M0チェックリスト
        ==========================================================================================*/
        // M0チェックリスト
        {
          path: "addon/secure-dataflow/check-list/m0-check-list",
          name: "addon-secure-dataflow-check-list-m0-check-list",
          component: () =>
            import(
              "./views/enterprise/addon/secure-dataflow/check-list/check-list-report/M0CheckListReport"
            ),
          meta: {
            isSecureDataflow: true,
          },
        },
        // M0チェックリスト トレンドデータ
        {
          path: "addon/secure-dataflow/check-list/trend-data-report",
          name: "addon-secure-dataflow-check-list-trend-data-report",
          component: () =>
            import(
              "./views/enterprise/addon/secure-dataflow/check-list/trend-data-report/TrendDataReport"
            ),
          meta: {
            isSecureDataflow: true,
          },
        },
        /*=========================================================================================
          機能拡張 -> アラート/イベントサマリー
        ==========================================================================================*/
        // アラートサマリー
        {
          path: "addon/secure-dataflow/alert-summary",
          name: "addon-secure-dataflow-alert-summary",
          component: () =>
            import(
              "./views/enterprise/addon/secure-dataflow/alert-summary/alertSummary"
            ),
          meta: {
            isSecureDataflow: true,
          },
        },
        // イベントサマリー
        {
          path: "addon/secure-dataflow/event-summary",
          name: "addon-secure-dataflow-event-summary",
          component: () =>
            import(
              "./views/enterprise/addon/secure-dataflow/event-summary/eventSummary"
            ),
          meta: {
            isSecureDataflow: true,
          },
        },
        //タスクマネージャー
        {
          path: "/enterprise/task-manager",
          name: "task-manager",
          component: () => import("./views/task-manager/TaskManager.vue"),
          meta: {
            breadcrumb: [
              {
                title: "ユーティリティ",
                color: "#2D9CDB",
              },
              { title: "タスクマネージャー", active: true },
            ],
            pageTitle: "タスクマネージャー",
          },
        },
        /*=========================================================================================
          機能拡張 -> 損害対応
        ==========================================================================================*/
        // AL-1 事故一覧
        {
          path: "addon/hull-insurance/claim-report-list",
          name: "hull-insurance-claim-report-list",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-report-list/ClaimReportList.vue"
            ),
          meta: {
            pageTitle: "事故一覧",
          },
        },
        // AR-1 事故報告
        {
          path: "addon/hull-insurance/claim-report-add",
          name: "hull-insurance-claim-report-add",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-report-add/ClaimReportAdd.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "事故報告ページ作成",
                active: true,
              },
            ],
            pageTitle: "事故報告情報",
          },
        },
        {
          path: "addon/hull-insurance/claim-report-add-confirm",
          name: "hull-insurance-claim-report-add-confirm",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-report-add/ClaimReportAddConfirm.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "事故報告ページ作成",
                active: true,
              },
            ],
            pageTitle: "事故報告情報",
          },
        },
        {
          path: "addon/hull-insurance/claim-report-add-complete",
          name: "hull-insurance-claim-report-add-complete",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-report-add/ClaimReportAddComplete.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "事故報告ページ作成",
                active: true,
              },
            ],
            pageTitle: "事故報告情報",
          },
        },
        // AD-1 事故ページ閲覧
        {
          path: "addon/hull-insurance/claim-report-view/:id",
          name: "hull-insurance-claim-report-view",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-report-view/ClaimReportView.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "事故ページ",
                active: true,
              },
            ],
            pageTitle: "事故ページ",
          },
        },
        // MM-1 マスター管理
        {
          path: "addon/hull-insurance/template-manager",
          name: "hull-insurance-template-manager",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/template-manager/TemplateManager.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "マスター管理",
                active: true,
              },
            ],
            pageTitle: "マスター管理",
          },
        },
        // MN-1 必要情報マスター一覧
        {
          path: "addon/hull-insurance/required-info-list",
          name: "hull-insurance-required-info-list",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/required-info-list/RequiredInfoList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "マスター管理",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-template-manager" },
              },
              {
                title: "必要情報マスター一覧",
                active: true,
              },
            ],
            pageTitle: "必要情報マスター一覧",
          },
        },
        // MS-1 損害種類一覧
        {
          path: "addon/hull-insurance/claim-type-list",
          name: "hull-insurance-claim-type-list",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-type-list/ClaimTypeList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "マスター管理",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-template-manager" },
              },
              {
                title: "損種テンプレート一覧",
                active: true,
              },
            ],
            pageTitle: "損種テンプレート一覧",
          },
        },
        // TAF-1 事故ページテンプレート登録
        {
          path: "addon/hull-insurance/claim-template/:id",
          name: "hull-insurance-claim-template",
          component: () =>
            import(
              "./views/enterprise/addon/hull-insurance/claim-template/ClaimTemplate.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "事故一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-report-list" },
              },
              {
                title: "マスター管理",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-template-manager" },
              },
              {
                title: "損害種類一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "hull-insurance-claim-type-list" },
              },
              {
                title: "テンプレート登録",
                active: true,
              },
            ],
            pageTitle: "損種（必要情報）テンプレート登録",
          },
        },
        // 省エネレポート船舶一覧
        {
          path: "addon/fuel-consumption-vessel-list",
          name: "fuel-consumption-vessel-list",
          component: () =>
            import(
              "./views/enterprise/addon/fuel-consumption/fuel-consumption-vessel-list/FuelConsumptionVesselList.vue"
            ),
          meta: {
            pageTitle: "省エネレポート",
            breadcrumb: [
              {
                title: "船舶一覧",
                active: true,
              },
            ],
          },
        },
        // 省エネレポート航海一覧
        {
          path: "addon/fuel-consumption-voyage-list/:vesselId",
          name: "fuel-consumption-voyage-list",
          component: () =>
            import(
              "./views/enterprise/addon/fuel-consumption/fuel-consumption-voyage-list/FuelConsumptionVoyageList.vue"
            ),
          meta: {
            pageTitle: "省エネレポート",
            breadcrumb: [
              {
                title: "船舶一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "fuel-consumption-vessel-list" },
              },
              {
                title: "航海一覧",
                active: true,
              },
            ],
          },
        },
        //省エネレポート詳細（航路別）
        {
          path: "addon/fuel-consumption-detail/:vesselId/:voyageId",
          name: "fuel-consumption-detail-voyage",
          component: () =>
            import(
              "./views/enterprise/addon/fuel-consumption/fuel-consumption-detail/voyage/FuelConsumptionDetailVoyage.vue"
            ),
          meta: {
            pageTitle: "省エネレポート",
            breadcrumb: [
              {
                title: "船舶一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "fuel-consumption-vessel-list" },
              },
              {
                title: "航海一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "fuel-consumption-voyage-list" },
              },
              {
                title: "省エネレポート",
                active: true,
              },
            ],
          },
        },
        //省エネレポート詳細（月次）
        {
          path: "addon/fuel-consumption-detail/:vesselId/period/:year/:month",
          name: "fuel-consumption-detail-monthly",
          component: () =>
            import(
              "./views/enterprise/addon/fuel-consumption/fuel-consumption-detail/period/FuelConsumptionDetailPeriod.vue"
            ),
          meta: {
            pageTitle: "省エネレポート",
            breadcrumb: [
              {
                title: "船舶一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "fuel-consumption-vessel-list" },
              },
              {
                title: "省エネレポート",
                active: true,
              },
            ],
          },
        },
        //省エネレポート詳細（年次）
        {
          path: "addon/fuel-consumption-detail/:vesselId/period/:year",
          name: "fuel-consumption-detail-yearly",
          component: () =>
            import(
              "./views/enterprise/addon/fuel-consumption/fuel-consumption-detail/period/FuelConsumptionDetailPeriod.vue"
            ),
          meta: {
            pageTitle: "省エネレポート",
            breadcrumb: [
              {
                title: "船舶一覧",
                color: ColorMixin.data().AISEA_COLOR.BLUE,
                url: { name: "fuel-consumption-vessel-list" },
              },
              {
                title: "省エネレポート",
                active: true,
              },
            ],
          },
        },
        /*=========================================================================================
              機能拡張 -> Operation
        ==========================================================================================*/
        {
          path: "addon/operation",
          name: "addon-operation",
          component: () =>
            import(
              "./views/enterprise/addon/operation/components/OperationCommonSetup.vue"
            ),
          redirect: {
            name: "addon-operation-reports-index",
          },

          children: [
            {
              path: "reports",
              name: "addon-operation-reports-index",
              component: () =>
                import("./views/enterprise/addon/operation/reports/list"),
              meta: {
                pageTitle: "レポート一覧",
              },
            },
            {
              path: "",
              component: () =>
                import(
                  "./views/enterprise/addon/operation/components/MasterDataFetcherIfNotStored.vue"
                ),
              children: [
                {
                  path: "reports/:aoEnterpriseId/:reportId",
                  name: "addon-operation-reports-show",
                  component: () =>
                    import(
                      "./views/enterprise/addon/operation/reports/show/OperationReportShow"
                    ),
                  meta: {
                    breadcrumb: [
                      {
                        title: "レポート一覧",
                        color: ColorMixin.data().AISEA_COLOR.BLUE,
                        url: { name: "addon-operation-reports-index" },
                      },
                      {
                        title: "レポート詳細",
                        active: true,
                      },
                    ],
                    pageTitle: "レポート詳細",
                  },
                },
                // 編集モード
                {
                  path: "reports/:aoEnterpriseId/:reportId/edit",
                  name: "addon-operation-reports-edit",
                  component: () =>
                    import(
                      "./views/enterprise/addon/operation/reports/show/OperationReportShow"
                    ),
                  meta: {
                    breadcrumb: [
                      {
                        title: "レポート一覧",
                        color: ColorMixin.data().AISEA_COLOR.BLUE,
                        url: { name: "addon-operation-reports-index" },
                      },
                      {
                        title: "レポート詳細",
                        active: true,
                      },
                    ],
                    pageTitle: "レポート詳細",
                  },
                },
              ],
            },
            // fallback of operation's routes
            {
              path: "*",
              name: "addon-operation-fallback",
              redirect: {
                name: "addon-operation-reports-index",
              },
            },
          ],
        },
        /*=========================================================================================
          機能拡張 -> GPRG/RgObjectHistory
         ==========================================================================================*/
        {
          path: "addon/radar-grid/rg-object-history-player",
          name: "addon-radar-grid-rg-object-history-player",
          component: () =>
            import(
              "./views/enterprise/addon/radar-grid/rg-object-history/RgObjectHistoryPlayer.vue"
            ),
        },
        {
          path: "error-404",
          name: "error-404",
          component: () => import("@/views/Error404.vue"),
        },
      ],
    },

    // -- Marina
    {
      path: "/marina",
      name: "Marina",
      component: () => import("./layouts/main/Main.vue"),
      beforeEnter: function(to, from, next) {
        return sessionUpdate().then(() => {
          if (Store.state.session.session.is_marina) {
            next();
          } else {
            next("/");
          }
        });
      },
      children: [
        {
          path: "dashboard",
          name: "marina-dashboard",
          component: () => import("./views/marina/Dashboard.vue"),
        },
        // 所属ユーザー管理
        {
          path: "user-list",
          name: "marina-user-list",
          component: () =>
            import("./views/marina/users/user-list/UserList.vue"),
        },
        {
          path: "user-view",
          name: "marina-user-view",
          component: () => import("./views/marina/users/UserView.vue"),
        },
      ],
    },
    /*=========================================================================================
      エラー
     ==========================================================================================*/
    // 旧 URL 互換: /error-404 への直リンクは /enterprise/error-404 (Main レイアウト) に転送する
    {
      path: "/error-404",
      redirect: "/enterprise/error-404",
    },
    {
      path: "/under-development",
      name: "under-development",
      component: () => import("@/views/UnderDevelopment.vue"),
    },
    // {
    //   path: '/pages/error-500',
    //   name: 'page-error-500',
    //   component: () => import('@/views/pages/Error500.vue'),
    //   meta: {
    //     rule: 'editor'
    //   }
    // },
    // 未マッチURLはすべて 404 へ
    {
      path: "*",
      redirect: "/enterprise/error-404",
    },
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

/** operation-frontend からの遷移時の db.auth 再構築処理が並行して走らないようにするフラグ */
let rehydrating = false;

router.beforeEach((to, from, next) => {
  if (to.path.startsWith(ENTERPRISE_INVITATES_PATH)) return next();
  if (to.path.startsWith(ENTERPRISE_ADD_PATH)) return next();
  if (to.path.startsWith("/admin-account-certification")) return next();
  if (to.query && to.query.logout === "force") return next();

  // 機能権限チェック: meta.featureCode またはパス対応表で featureCode を解決し、
  // enterprise_enabled=false または user_enabled=false のとき 404 へリダイレクトする。
  // 権限ツリーが未取得の場合はスキップ（VerticalNavMenu が初回ログイン後に非同期取得する）。
  const featureCode =
    (to.meta && to.meta.featureCode) || getFeatureCodeForPath(to.path);
  if (featureCode) {
    const permMap = Store.getters.featurePermissionMap;
    if (permMap && Object.keys(permMap).length > 0) {
      const perm = permMap[featureCode];
      if (perm && (!perm.enterprise_enabled || !perm.user_enabled)) {
        return next({ name: "error-404" });
      }
    }
  }

  // >>> auth-trace (検証用、後で `>>> auth-trace` ～ `<<< auth-trace` を含む全ブロックを削除)
  Vue.$log.info("[auth-trace] beforeEach enter", {
    from: from.fullPath,
    to: to.fullPath,
    currentUser: !!firebase.auth().currentUser,
    lastLoginSuccess: Store.state.auth.lastLoginSuccess,
    ts: Date.now(),
  });
  // <<< auth-trace

  // 毎 beforeEach で登録されるリスナーが累積しないよう一度だけ発火で unsubscribe する
  let fired = false;
  const unsubscribe = firebase.auth().onAuthStateChanged(async user => {
    if (fired) return;
    fired = true;
    unsubscribe();

    // >>> auth-trace
    const deltaMs = Store.state.auth.lastLoginSuccess
      ? Date.now() - moment(Store.state.auth.lastLoginSuccess).valueOf()
      : null;
    Vue.$log.info("[auth-trace] onAuthStateChanged fired", {
      user: !!user,
      uid: user ? user.uid : null,
      to: to.fullPath,
      lastLoginSuccess: Store.state.auth.lastLoginSuccess,
      deltaMs,
      ts: Date.now(),
    });
    // <<< auth-trace

    if (user) {
      authService.hasUserInfoInLocalDb().then(async res => {
        // >>> auth-trace
        Vue.$log.info("[auth-trace] hasUserInfoInLocalDb", {
          hasIdToken: res,
          to: to.fullPath,
        });
        // <<< auth-trace

        if (res) {
          if (Store.state.auth.user === null) {
            authService.getAddons().then(res => {
              Store.commit("SET_ADDONS", { addons: res });
            });
          }

          // User設定
          if (Store.state.auth.user === null) {
            authService.getUser().then(res => {
              Store.commit("SET_USER", { user: res });
            });
          }

          // 有料・無料設定
          if (Store.state.auth.user === null) {
            const isFree = await authService.getIsFree();
            if (isFree !== null) Store.commit("SET_IS_FREE", { isFree });
          }
          // アカウント設定
          if (Store.state.auth.accountId === null) {
            await Store.dispatch("auth/setAccountId");
          }

          // IDトークンリフレッシュ
          if (
            moment().valueOf() -
              moment(Store.state.auth.lastLoginSuccess).valueOf() <
            8 * 1000
          ) {
            // >>> auth-trace
            Vue.$log.info("[auth-trace] branch ① grace period → next()");
            // <<< auth-trace
            // ログイン時とログイン直後は、moduleAuthActions が IDトークンの書き換えを行っているので、素通りさせる。
            return next();
          } else if (
            to.path === "/login/enterprise" ||
            from.path === "/login/enterprise" ||
            to.path === "/login/mfa" ||
            from.path === "/login/mfa"
          ) {
            // 既に /enterprise/live-map に向かっている場合は同 target redirect で
            // vue-router の Redirected ループに陥るので素通しする
            if (to.path === "/enterprise/live-map") {
              return next();
            }

            return next({
              path: "/enterprise/live-map",
            });
          } else {
            // renewToken は securetoken 失敗時に Firebase の生セッションからの
            // 再取得まで試みる。false = Firebase セッションも切れ、の場合のみ
            // 強制ログアウトする。古い idToken のまま next() しない（403 ループ防止）。
            const renewed = await authService.renewToken();
            if (!renewed) {
              // >>> auth-trace
              Vue.$log.warn(
                "[auth-trace] branch ① renewToken failed (firebase session dead) → cleanup + force logout",
                { to: to.fullPath }
              );
              // <<< auth-trace
              await Store.dispatch("auth/cleanupLocalData");
              return next({
                name: "login-enterprise",
                query: { logout: "force" },
              });
            }
            const t = await authService.getIdToken();
            Store.commit("SET_ID_TOKEN", { idToken: t });
            // 無料ユーザが許可された画面以外に遷移しようとする場合、crew-status-listに遷移させる
            if (!Store.getters.isPaid && !isAllowPath(to.path)) {
              return next({
                path: "/enterprise/management/crews/crew-status-list",
              });
            }
            return next();
          }
        } else {
          // firebaseのuserは存在するが、db.auth に idToken がない場合（operation 等から遷移してきたケース）
          // >>> auth-trace
          Vue.$log.info(
            "[auth-trace] branch ② entered (user=truthy, no idToken)",
            {
              to: to.fullPath,
            }
          );
          // <<< auth-trace

          // ログイン直後（8秒以内）は moduleAuthActions 側で db.auth 書き込み処理中のため素通しする
          if (
            Store.state.auth.lastLoginSuccess &&
            moment().valueOf() -
              moment(Store.state.auth.lastLoginSuccess).valueOf() <
              8 * 1000
          ) {
            // >>> auth-trace
            Vue.$log.info("[auth-trace] branch ② grace period → next()");
            // <<< auth-trace
            return next();
          }

          if (rehydrating) {
            // >>> auth-trace
            Vue.$log.info("[auth-trace] branch ② already rehydrating → next()");
            // <<< auth-trace
            return next();
          }
          rehydrating = true;
          // localStorage['user'] から UserInfo を取得して db.auth を再構築
          try {
            // localStorage['user'] から UserInfo を取得
            const localStorageUser = getCachedUser();

            // >>> auth-trace
            Vue.$log.info("[auth-trace] branch ② getCachedUser", {
              hasUser: !!localStorageUser,
              hasEnterpriseId: !!(
                localStorageUser && localStorageUser.enterpriseId
              ),
            });
            // <<< auth-trace

            // UserInfo が存在する場合
            if (localStorageUser && localStorageUser.enterpriseId) {
              // >>> auth-trace
              Vue.$log.info("[auth-trace] branch ② rehydrating db.auth", {
                enterpriseId: localStorageUser.enterpriseId,
                accountId: localStorageUser.accountId,
              });
              // <<< auth-trace
              const idToken = await user.getIdToken(true);
              const refreshToken = user.refreshToken;
              const enterpriseData = {
                id: localStorageUser.enterpriseId,
                name: localStorageUser.enterpriseName,
                login_key: localStorageUser.loginKey,
                account_id: localStorageUser.accountId,
                is_first_login: false,
              };
              await authService.saveAuthInfo({
                idToken,
                refreshToken,
                user: enterpriseData,
                addons: localStorageUser.addons,
                isFree: localStorageUser.isFree,
                rememberIds: { userId: null, enterpriseId: null },
              });
              Store.commit("SET_ID_TOKEN", { idToken });
              Store.commit("SET_ADDONS", { addons: localStorageUser.addons });
              Store.commit("SET_USER", { user: enterpriseData });
              Store.commit("SET_IS_FREE", { isFree: localStorageUser.isFree });
              Vue.$log.info(
                "[router] db.auth rebuilt from BroadcastChannel response"
              );
              // 無料ユーザが許可された画面以外に遷移しようとする場合、crew-status-listに遷移させる
              if (!Store.getters.isPaid && !isAllowPath(to.path)) {
                return next({
                  path: "/enterprise/management/crews/crew-status-list",
                });
              }
              return next();
            } else {
              // 応答なし or UID 不一致 → ログイン画面へ
              Vue.$log.warn(
                "[router] BroadcastChannel miss — redirecting to login"
              );
              // signOut が後追いで次のログインに刺さらないよう、redirect 前に完了を待つ
              await Store.dispatch("auth/cleanupLocalData");
              next({
                name: "login-enterprise",
                query: { logout: "force" },
              });
            }
          } finally {
            rehydrating = false;
          }
        }
      });
    } else {
      // >>> auth-trace
      Vue.$log.warn("[auth-trace] branch ③ entered (user=null)", {
        to: to.fullPath,
        from: from.fullPath,
        lastLoginSuccess: Store.state.auth.lastLoginSuccess,
        deltaMs,
      });
      // <<< auth-trace
      const mfa = await authService.getMfa();
      if (mfa && to.path.startsWith("/login/mfa")) {
        // >>> auth-trace
        Vue.$log.info("[auth-trace] branch ③ MFA path → next()");
        // <<< auth-trace
        return next();
      }
      Vue.$log.info("== User Logout ==");
      // signOut が後追いで次のログインに刺さらないよう、redirect 前に完了を待つ
      await Store.dispatch("auth/cleanupLocalData");
      if (!to.path.startsWith("/login/enterprise")) {
        return next({ name: "login-enterprise", query: { logout: "force" } });
      }
      return next();
    }
  });
});

const sessionUpdate = () => {
  return Store.dispatch("session/update").then(() =>
    Store.dispatch("session/reLogin")
  );
};

const isAllowPath = (path, allows = FREE_PATHS) => {
  return allows.some(ap => path.startsWith(ap));
};

export default router;
